import { useEffect, useContext } from "react"
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UserContext } from 'ui-seeker/core'
import { getAppUserData } from "app/@utils"
import { FEATURES, USER_ROLE } from "app/@common/constants"

const WIDGET_SRC = 'https://pshotels-332887153994.us-central1.run.app/widget.js'
const SESSION_PATHS = ['/login', '/login-house', '/logout', '/forgot-password', '/reset-password', '/404']
// Set on <html> while the drawer may narrow the page; the stylesheet keys off it
// (style/shared/_iaAssistant.scss). Ours, not the widget's own `dsb-pushed`.
const PUSH_CLASS = 'ds-ia-push'
// Room the dashboard needs to the left of the panel before narrowing is worth it.
// Measured on Competition: ~1010px already breaks the filter bar, full width is fine.
const MIN_CONTENT_WIDTH = 1280

function resolveWidgetUsername(role, uidSlave, userId, username) {
    if (role !== USER_ROLE.MASTER) return username
    const slaveId = parseInt(uidSlave)
    const masterId = parseInt(userId)
    if (!slaveId || slaveId === masterId) return username
    try {
        const selectorData = JSON.parse(localStorage.getItem('selectorFeaturesData') || '{}')
        const slave = (selectorData?.all || []).find(s => parseInt(s.value) === slaveId)
        if (slave?.label) return slave.label
    } catch (_) { /* fall back to master username */ }
    return username
}

function removeWidget() {
    // Give back the room the drawer was holding before the DOM goes: with data-push the
    // widget keeps an inline margin on <html>, and pulling its nodes out by hand (as a
    // language change does) would leave the page narrowed with no panel beside it.
    // Its own destroy() clears that margin and the dsb-pushed class; the manual reset
    // below covers the case where the script never finished loading.
    try { window.DSB?.destroy() } catch (_) { /* the widget is already gone */ }
    const html = document.documentElement
    html.classList.remove(PUSH_CLASS, 'dsb-pushed')
    html.style.marginRight = ''
    html.style.marginLeft = ''

    document.querySelectorAll('.dsb-btn, .dsb-panel').forEach(el => {
        if (el.parentNode) el.parentNode.removeChild(el)
    })
    const style = document.getElementById('dsb-root')
    if (style && style.parentNode) style.parentNode.removeChild(style)
    const existing = document.querySelector(`script[src="${WIDGET_SRC}"]`)
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing)
}

export default function IaAssistantWidget() {
    // Subscribes to changeLanguage so the language and the title read below are
    // re-evaluated without a reload.
    const { t } = useTranslation()
    const { isLogged } = useContext(UserContext)
    const { pathname } = useLocation()
    const { features, uidSlave, userId, username, role, language } = getAppUserData()
    const slaveFeatures = features?.[uidSlave] || []
    const hasFeature = slaveFeatures.includes(FEATURES.IA_ASSISTANT)
    const isSessionPage = SESSION_PATHS.some(p => pathname.startsWith(p))
    const widgetUsername = !isSessionPage && isLogged && hasFeature && username
        ? resolveWidgetUsername(role, uidSlave, userId, username)
        : null

    useEffect(() => {
        if (!widgetUsername) {
            removeWidget()
            return
        }
        // The widget reads data-lang only once, at load: a language change re-injects it.
        const existing = document.querySelector(`script[src="${WIDGET_SRC}"]`)
        if (existing) {
            if ((existing.dataset.lang || '') === (language || '')) return
            removeWidget()
        }

        const script = document.createElement('script')
        script.src = WIDGET_SRC
        script.async = true
        script.setAttribute('data-agent', 'pshotels')
        script.setAttribute('data-app', 'hotels-webapp')
        script.setAttribute('data-username', widgetUsername)
        // Brand: Paraty decided the assistant carries no product name of its own, so the
        // header states what it is, matching the public landing page. Still explicit
        // because the agent's own default would otherwise read "DS IA".
        // Bubble, header icon and reply avatar are left to the widget's own defaults.
        script.setAttribute('data-title', t('common:iaAssistant.title'))
        // Drawer + push: the panel narrows the page instead of covering it
        script.setAttribute('data-layout', 'drawer')
        script.setAttribute('data-position', 'right')
        script.setAttribute('data-push', 'true')
        if (language) script.setAttribute('data-lang', language)
        document.body.appendChild(script)
    }, [widgetUsername, language, t])

    // Push watcher, deliberately in its own effect: the one above returns early when the
    // script is already mounted, so an observer set up there would be torn down by a
    // re-run and never rebuilt, leaving the drawer unable to narrow the page.
    //
    // The widget's data-push writes a margin on <html>, but ui-seeker's reboot pins
    // `#root, body, html { width: 100% }`, so the page ignores it. The stylesheet frees
    // that width — only while PUSH_CLASS is set, which is this watcher's job: narrowing
    // the page is only an improvement when what is left still fits the dashboard. Below
    // MIN_CONTENT_WIDTH the filter bar collapses (title wraps over the tour link, hotel
    // chips stack three rows deep), so there we leave the panel overlaying instead.
    useEffect(() => {
        const syncPushRoom = () => {
            const html = document.documentElement
            const pushed = parseInt(html.style.marginRight, 10) || parseInt(html.style.marginLeft, 10) || 0
            const fits = pushed > 0 && (window.innerWidth - pushed) >= MIN_CONTENT_WIDTH
            if (html.classList.contains(PUSH_CLASS) !== fits) html.classList.toggle(PUSH_CLASS, fits)
        }
        // The panel is resizable by its grip and the margin follows it, so watch the
        // attribute rather than just the open/close events. The comparison above guards
        // the observer against re-triggering itself on our own class change.
        const observer = new MutationObserver(syncPushRoom)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] })
        window.addEventListener('resize', syncPushRoom)
        syncPushRoom()

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', syncPushRoom)
            document.documentElement.classList.remove(PUSH_CLASS)
        }
    }, [])

    return null
}
